import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import { DateFilter } from "../../components/DateFilter";
import AlertGrid from "./AlertGrid";
import AlertDetailsModal from "./AlertDetailsModal";
import { alertService } from "../../Services/alert.service";

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

export default function AlertsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  dispatch({ type: "title", value: "Alertas" });

  const [startDate, setStartDate] = useState(new Date(Date.now() - THREE_DAYS_MS));
  const [endDate, setEndDate] = useState(new Date());

  const [gridInfo, setGridInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [toastParams, setToastParams] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenedProtocolIdRef = useRef(null);

  const fetchAlertsData = async (showSpinner) => {
    if (showSpinner) setIsLoading(true);
    try {
      const result = await alertService.fetchAlerts({
        StartDate: startDate?.toISOString(),
        EndDate: endDate?.toISOString(),
      });

      if (result === null) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("ALERTS.TITLE"),
            body: t("REPORTS.ERROR_FETCHING_REPORT"),
            color: "Warning",
          })
        );
        setGridInfo([]);
      } else {
        setGridInfo(result);
      }
    } finally {
      if (showSpinner) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAlertsData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    if (selectedAlert) return;
    const intervalId = setInterval(() => fetchAlertsData(false), 10000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedAlert]);

  useEffect(() => {
    const protocolId = searchParams.get("protocolId");
    if (!protocolId) {
      autoOpenedProtocolIdRef.current = null;
      return;
    }
    if (!Array.isArray(gridInfo) || gridInfo.length === 0) return;
    if (autoOpenedProtocolIdRef.current === protocolId) return;

    autoOpenedProtocolIdRef.current = protocolId;

    const match = gridInfo.find((item) => String(item.protocolId) === String(protocolId));
    if (match) setSelectedAlert(match);
  }, [gridInfo, searchParams]);

  const handleViewDetails = (item) => {
    setSelectedAlert(item);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("protocolId", item.protocolId);
      return next;
    });
  };

  const handleCloseModal = () => {
    setSelectedAlert(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("protocolId");
      return next;
    });
  };

  const handleStatusUpdated = (protocolId, newStatus) => {
    if (!newStatus) return;
    setGridInfo((list) =>
      (list || []).map((item) =>
        item.protocolId === protocolId
          ? {
            ...item,
            protocolStatusId: newStatus.protocolStatusId,
            protocolStatus: newStatus.protocolStatus,
          }
          : item
      )
    );
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <div className="row mb-4 align-items-end g-3">
        <div className="col-md-6 col-12">
          <DateFilter
            id="alerts-start-date"
            title={t("ALERTS.FILTERS.START_DATE")}
            value={startDate}
            handleCallback={setStartDate}
            step={30}
          />
        </div>
        <div className="col-md-6 col-12">
          <DateFilter
            id="alerts-end-date"
            title={t("ALERTS.FILTERS.END_DATE")}
            value={endDate}
            handleCallback={setEndDate}
            step={30}
          />
        </div>
      </div>

      {gridInfo && <AlertGrid gridData={gridInfo} onViewDetails={handleViewDetails} />}
      {isLoading && <Spinner />}

      <AlertDetailsModal
        show={!!selectedAlert}
        alert={selectedAlert}
        handleClose={handleCloseModal}
        onStatusUpdated={handleStatusUpdated}
      />
    </div>
  );
}
